import React, { useState, useEffect, useRef } from 'react';
import FileUpload from './components/FileUpload';
import UploadStatus from './components/UploadStatus';
import ProcessingStatus from './components/ProcessingStatus';
import apiService from './services/api';
import { FileText, Zap, Download } from 'lucide-react';
import { ExportXlsxResponse, JobStatus, JobResponse } from './types';

function App(): React.JSX.Element {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadResults, setUploadResults] = useState<Array<{
    originalFileName: string;
    uniqueFileName: string;
    uploadUrl: string;
  }>>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [processingProgress, setProcessingProgress] = useState<number>(0);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [exportResult, setExportResult] = useState<ExportXlsxResponse | null>(null);
  // Async job state
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [jobProgress, setJobProgress] = useState<number>(0);
  const [filesCount, setFilesCount] = useState<number>(0);
  const [processedFilesCount, setProcessedFilesCount] = useState<number>(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleFilesSelected = async (files: File[]): Promise<void> => {
    setSelectedFiles(files);
    setError(null);
    setIsUploading(true);
    setUploadProgress(0);
    setUploadResults([]);

    try {
      // Upload files
      const result = await apiService.uploadFiles(files);
      
      if (result.success && result.data) {
        setUploadProgress(100);
        setUploadResults(result.data);
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      setError('An unexpected error occurred during upload.');
      console.error('Upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleProcessFiles = async (): Promise<void> => {
    if (uploadResults.length === 0) return;

    setIsProcessing(true);
    setProcessingProgress(0);
    setProcessingError(null);
    setExportResult(null);
    setCurrentJobId(null);
    setJobStatus(null);
    setJobProgress(0);
    setFilesCount(0);
    setProcessedFilesCount(0);

    try {
      // Extract unique file names from upload results
      const fileNames = uploadResults.map(result => result.uniqueFileName);
      
      // Start async export job
      const startResult = await apiService.startExport(fileNames);
      
      if (startResult.success && startResult.data?.jobId) {
        const jobId = startResult.data.jobId;
        setCurrentJobId(jobId);
        setJobStatus('IN_PROGRESS');
        
        // Start polling for job status
        pollJobStatus(jobId);
      } else {
        setProcessingError(startResult.error || 'Failed to start export job');
        setIsProcessing(false);
      }
    } catch (err) {
      setProcessingError('An unexpected error occurred during processing.');
      console.error('Processing error:', err);
      setIsProcessing(false);
    }
  };

  const pollJobStatus = (jobId: string): void => {
    // Poll immediately first time
    checkJobStatus(jobId);
    
    // Then poll every 3 seconds
    pollingIntervalRef.current = setInterval(() => {
      checkJobStatus(jobId);
    }, 3000);
  };

  const checkJobStatus = async (jobId: string): Promise<void> => {
    try {
      const statusResult = await apiService.getExportStatus(jobId);
      
      if (statusResult.success && statusResult.data) {
        const jobData: JobResponse = statusResult.data;
        setJobStatus(jobData.status);
        setFilesCount(jobData.filesCount);
        setProcessedFilesCount(jobData.processedFilesCount);
        setJobProgress(jobData.progress);
        setProcessingProgress(Math.round(jobData.progress * 100));
        
        // If job is completed, stop polling and set result
        if (jobData.status === 'COMPLETED') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          setIsProcessing(false);
          if (jobData.downloadUrl) {
            setExportResult({
              downloadUrl: jobData.downloadUrl,
              filesProcessed: jobData.filesCount,
              message: `Successfully processed ${jobData.filesCount} file(s)`
            });
          }
        } 
        // If job failed, stop polling and show error
        else if (jobData.status === 'FAILED') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          setIsProcessing(false);
          setProcessingError(jobData.error || 'Job processing failed');
        }
        // IN_PROGRESS - continue polling (already set up)
      } else {
        // Error getting status - might be temporary, log but continue polling
        console.warn('Failed to get job status:', statusResult.error);
      }
    } catch (err) {
      console.error('Error checking job status:', err);
      // Don't stop polling on network errors - might be temporary
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleDownloadExcel = (): void => {
    if (exportResult?.downloadUrl) {
      window.open(exportResult.downloadUrl, '_blank');
    }
  };

  const handleReset = (): void => {
    // Stop polling if active
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    setSelectedFiles([]);
    setIsUploading(false);
    setUploadProgress(0);
    setError(null);
    setUploadResults([]);
    setIsProcessing(false);
    setProcessingProgress(0);
    setProcessingError(null);
    setExportResult(null);
    // Reset job state
    setCurrentJobId(null);
    setJobStatus(null);
    setJobProgress(0);
    setFilesCount(0);
    setProcessedFilesCount(0);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg">
              <FileText className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">EntryAssist.io</h1>
              <p className="text-sm text-gray-600">Extract data from PDF invoices using AI</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Features Section */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">How it works</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-3">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-medium text-gray-900 mb-1">1. Upload PDFs</h3>
                <p className="text-sm text-gray-600">Upload one or more PDF invoice files</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg mx-auto mb-3">
                  <Zap className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="font-medium text-gray-900 mb-1">2. AI Processing</h3>
                <p className="text-sm text-gray-600">Our AI extracts key data from your invoices</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mx-auto mb-3">
                  <Download className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-medium text-gray-900 mb-1">3. Download Excel</h3>
                <p className="text-sm text-gray-600">Download your processed data as an Excel file</p>
              </div>
            </div>
          </div>

          {/* Upload Section */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload PDF Files</h2>
            <FileUpload 
              onFilesSelected={handleFilesSelected}
              isLoading={isUploading}
            />
            
            {selectedFiles.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Selected Files:</h3>
                <div className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <FileText className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-700">{file.name}</span>
                      <span className="text-xs text-gray-500">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Upload Status */}
          <UploadStatus
            isUploading={isUploading}
            progress={uploadProgress}
            error={error}
            uploadResults={uploadResults}
          />

          {/* Processing Section */}
          {uploadResults.length > 0 && !isProcessing && !exportResult && !processingError && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Process Files</h2>
              <p className="text-sm text-gray-600 mb-4">
                Your files have been uploaded successfully. Click the button below to process them and generate an Excel file.
              </p>
              <button
                onClick={handleProcessFiles}
                disabled={isProcessing}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Zap className="h-4 w-4 mr-2" />
                Process Files & Generate Excel
              </button>
            </div>
          )}

          {/* Processing Status */}
          <ProcessingStatus
            isProcessing={isProcessing}
            progress={processingProgress}
            error={processingError}
            exportResult={exportResult}
            onDownload={handleDownloadExcel}
            jobStatus={jobStatus}
            filesCount={filesCount}
            processedFilesCount={processedFilesCount}
          />

          {/* Reset Button */}
          {(uploadResults.length > 0 || error || exportResult || processingError) && (
            <div className="text-center">
              <button
                onClick={handleReset}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Upload More Files
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-500">
            <p>Powered by Amazon Textract and AI technology</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;